(() => {
  'use strict';

  if (!('customElements' in window) || customElements.get('clairabord-story')) return;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const smoothstep = (value) => {
    const x = clamp(value);
    return x * x * (3 - 2 * x);
  };

  class ClairabordStory extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.heroVisual = this.querySelector('.cb-hero__visual');

      this.story = this.querySelector('[data-scroll-story]');
      this.stage = this.querySelector('[data-story-stage]');
      this.progressBar = this.querySelector('[data-story-progress]');
      this.steps = Array.from(this.querySelectorAll('[data-story-step]'));
      this.layers = this.stage ? Array.from(this.stage.querySelectorAll('[data-reveal]')) : [];

      this.collectionStory = this.querySelector('[data-collection-story]');
      this.collectionStack = this.querySelector('.cb-collection-story__stack');
      this.collectionFeatures = Array.from(this.querySelectorAll('[data-collection-feature]'));
      this.collectionProgressBar = this.querySelector('[data-collection-progress]');
      this.collectionCopy = this.querySelector('[data-collection-copy]');
      this.collectionFinal = this.querySelector('[data-collection-final]');
      this.collectionCounter = this.querySelector('[data-collection-counter]');
      this.collectionTitle = this.querySelector('[data-collection-title]');
      this.collectionDescription = this.querySelector('[data-collection-description]');

      this.dialog = this.querySelector('[data-collection-dialog]');
      this.carouselTrack = this.querySelector('[data-carousel-track]');
      this.carouselSlides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.carouselPrevious = this.querySelector('[data-carousel-previous]');
      this.carouselNext = this.querySelector('[data-carousel-next]');
      this.carouselStatus = this.querySelector('[data-carousel-status]');
      this.carouselIndex = 0;
      this.carouselFrameRequested = false;

      this.frameRequested = false;
      this.isNearStory = true;
      this.isNearCollection = true;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMotionPreference = this.handleMotionPreference.bind(this);
      this.openCarousel = this.openCarousel.bind(this);
      this.closeCarousel = this.closeCarousel.bind(this);
      this.handleCarouselScroll = this.handleCarouselScroll.bind(this);

      requestAnimationFrame(() => this.setAttribute('data-mounted', 'true'));
      this.setupHeroReveal();
      this.setupSectionObservers();
      this.setupCarousel();

      window.addEventListener('scroll', this.handleScroll, { passive: true, signal: this.signal });
      window.addEventListener('resize', this.handleResize, { passive: true, signal: this.signal });

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.handleMotionPreference);
      }

      this.update(true);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      if (this.dialog?.open) this.restorePageScroll();
      this.abortController.abort();
      if (this.heroObserver) this.heroObserver.disconnect();
      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (typeof this.reducedMotion?.removeEventListener === 'function') {
        this.reducedMotion.removeEventListener('change', this.handleMotionPreference);
      }
      this.initialized = false;
    }

    setupHeroReveal() {
      if (!this.heroVisual) return;
      if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.heroVisual.classList.add('is-intro-visible');
        return;
      }

      this.heroObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.heroVisual.classList.add('is-intro-visible');
          this.heroObserver.disconnect();
        },
        { threshold: 0.58, rootMargin: '0px' }
      );
      this.heroObserver.observe(this.heroVisual);
    }

    setupSectionObservers() {
      if (!('IntersectionObserver' in window)) return;
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.story) this.isNearStory = entry.isIntersecting;
            if (entry.target === this.collectionStory) this.isNearCollection = entry.isIntersecting;
          });
          if (this.isNearStory || this.isNearCollection) this.requestUpdate();
        },
        { rootMargin: '100% 0px 100% 0px' }
      );
      if (this.story) this.sectionObserver.observe(this.story);
      if (this.collectionStory) this.sectionObserver.observe(this.collectionStory);
    }

    setupCarousel() {
      const openButton = this.querySelector('[data-open-carousel]');
      const closeButton = this.querySelector('[data-close-carousel]');
      if (!this.dialog || !this.carouselTrack || !openButton) return;

      openButton.addEventListener('click', this.openCarousel, { signal: this.signal });
      closeButton?.addEventListener('click', this.closeCarousel, { signal: this.signal });
      this.carouselPrevious?.addEventListener('click', () => this.updateCarousel(this.carouselIndex - 1), { signal: this.signal });
      this.carouselNext?.addEventListener('click', () => this.updateCarousel(this.carouselIndex + 1), { signal: this.signal });
      this.carouselTrack.addEventListener('scroll', this.handleCarouselScroll, { passive: true, signal: this.signal });
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) this.closeCarousel();
        },
        { signal: this.signal }
      );
      this.dialog.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowLeft') this.updateCarousel(this.carouselIndex - 1);
          if (event.key === 'ArrowRight') this.updateCarousel(this.carouselIndex + 1);
        },
        { signal: this.signal }
      );
      this.dialog.addEventListener(
        'close',
        () => {
          this.restorePageScroll();
          this.lastFocusedElement?.focus();
        },
        { signal: this.signal }
      );
      this.syncCarouselControls();
    }

    openCarousel() {
      this.lastFocusedElement = document.activeElement;
      this.previousRootOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      requestAnimationFrame(() => this.updateCarousel(this.carouselIndex, false));
    }

    closeCarousel() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.restorePageScroll();
        this.lastFocusedElement?.focus();
      }
    }

    restorePageScroll() {
      document.documentElement.style.overflow = this.previousRootOverflow || '';
    }

    updateCarousel(index, smooth = true) {
      if (!this.carouselSlides.length) return;
      this.carouselIndex = clamp(index, 0, this.carouselSlides.length - 1);
      const slide = this.carouselSlides[this.carouselIndex];
      const left = slide.offsetLeft - (this.carouselTrack.clientWidth - slide.clientWidth) / 2;
      const behavior = smooth && !this.reducedMotion.matches ? 'smooth' : 'auto';
      if (typeof this.carouselTrack.scrollTo === 'function') this.carouselTrack.scrollTo({ left, behavior });
      else this.carouselTrack.scrollLeft = left;
      this.syncCarouselControls();
    }

    handleCarouselScroll() {
      if (this.carouselFrameRequested) return;
      this.carouselFrameRequested = true;
      requestAnimationFrame(() => {
        this.carouselFrameRequested = false;
        const center = this.carouselTrack.scrollLeft + this.carouselTrack.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;
        this.carouselSlides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
          const distance = Math.abs(center - slideCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        this.carouselIndex = closestIndex;
        this.syncCarouselControls();
      });
    }

    syncCarouselControls() {
      const total = this.carouselSlides.length;
      if (this.carouselPrevious) this.carouselPrevious.disabled = this.carouselIndex <= 0;
      if (this.carouselNext) this.carouselNext.disabled = this.carouselIndex >= total - 1;
      if (this.carouselStatus) {
        this.carouselStatus.textContent = `${String(this.carouselIndex + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      }
    }

    handleScroll() {
      if (this.isNearStory || this.isNearCollection) this.requestUpdate();
    }

    handleResize() {
      this.requestUpdate(true);
      if (this.dialog?.open) this.updateCarousel(this.carouselIndex, false);
    }

    handleMotionPreference() {
      if (this.reducedMotion.matches) this.heroVisual?.classList.add('is-intro-visible');
      this.update(true);
    }

    requestUpdate(force = false) {
      if (this.frameRequested && !force) return;
      this.frameRequested = true;
      requestAnimationFrame(() => {
        this.frameRequested = false;
        this.update(force);
      });
    }

    update(force = false) {
      this.updateStickerStory(force);
      this.updateCollectionStory(force);
    }

    updateStickerStory(force = false) {
      if (!this.story || (!this.isNearStory && !force)) return;

      const reduced = this.reducedMotion.matches;
      const rect = this.story.getBoundingClientRect();
      const scrollDistance = Math.max(1, this.story.offsetHeight - window.innerHeight);
      const progress = reduced ? 1 : clamp(-rect.top / scrollDistance);

      this.style.setProperty('--cb-story-progress', progress.toFixed(4));
      if (this.progressBar) this.progressBar.style.transform = `scaleX(${progress.toFixed(4)})`;

      const ranges = {
        top: [0.02, 0.2],
        background: [0.16, 0.36],
        avatar: [0.28, 0.54],
        bottom: [0.52, 0.76],
        wordmark: [0.74, 0.96]
      };

      this.layers.forEach((layer) => {
        const name = layer.dataset.reveal;
        const range = ranges[name] || [0, 1];
        const amount = reduced ? 1 : smoothstep((progress - range[0]) / (range[1] - range[0]));
        layer.style.opacity = amount.toFixed(4);

        if (name === 'background') {
          layer.style.transform = 'none';
          return;
        }

        const travel = name === 'avatar' ? 34 : 22;
        const offset = (1 - amount) * travel;
        const scale = 0.965 + amount * 0.035;
        layer.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
      });

      const activeIndex = reduced
        ? this.steps.length - 1
        : Math.min(this.steps.length - 1, Math.floor(progress * this.steps.length));

      this.steps.forEach((step, index) => {
        const active = index === activeIndex;
        step.classList.toggle('is-active', active);
        if (active) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
    }

    updateCollectionStory(force = false) {
      if (!this.collectionStory || !this.collectionFeatures.length || (!this.isNearCollection && !force)) return;

      const reduced = this.reducedMotion.matches;
      const rect = this.collectionStory.getBoundingClientRect();
      const scrollDistance = Math.max(1, this.collectionStory.offsetHeight - window.innerHeight);
      const progress = reduced ? 1 : clamp(-rect.top / scrollDistance);
      const total = this.collectionFeatures.length;

      this.style.setProperty('--cb-collection-progress', progress.toFixed(4));
      if (this.collectionProgressBar) {
        this.collectionProgressBar.style.transform = `scaleX(${progress.toFixed(4)})`;
      }

      if (reduced) {
        this.style.setProperty('--cb-orbit-visibility', '0');
        this.collectionFeatures.forEach((feature) => {
          feature.style.opacity = '1';
          feature.style.transform = 'none';
          feature.style.filter = 'none';
          feature.style.zIndex = '1';
          feature.classList.add('is-active');
          feature.setAttribute('aria-hidden', 'false');
        });
        if (this.collectionFinal) {
          this.collectionFinal.style.opacity = '1';
          this.collectionFinal.style.transform = 'none';
          this.collectionFinal.classList.add('is-visible');
          this.collectionFinal.setAttribute('aria-hidden', 'false');
        }
        return;
      }

      const orbitEnd = 0.7;
      const lineupStart = orbitEnd;
      const lineupEnd = 0.84;
      const exitStart = 0.86;
      const exitEnd = 0.95;
      const finalStart = 0.91;
      const orbitSlot = orbitEnd / total;
      const stackWidth = Math.max(320, this.collectionStack?.clientWidth || 720);
      const stackRect = this.collectionStack?.getBoundingClientRect();
      const stackCenter = stackRect ? stackRect.left + stackRect.width / 2 : window.innerWidth / 2;
      const lineupCenterShift = window.innerWidth / 2 - stackCenter;
      const orbitAmplitude = stackWidth * (window.innerWidth < 700 ? 0.25 : 0.3);
      const lineupSpacing = stackWidth * (window.innerWidth < 700 ? 0.23 : 0.24);
      const lineupScale = window.innerWidth < 700 ? 0.32 : 0.285;
      const lineupAmount = smoothstep((progress - lineupStart) / (lineupEnd - lineupStart));
      const exitAmount = smoothstep((progress - exitStart) / (exitEnd - exitStart));
      const finalAmount = smoothstep((progress - finalStart) / (1 - finalStart));
      const activeIndex = progress < orbitEnd
        ? Math.min(total - 1, Math.floor(progress / orbitSlot))
        : total - 1;

      this.style.setProperty('--cb-orbit-visibility', (1 - smoothstep((progress - 0.65) / 0.11)).toFixed(4));

      this.collectionFeatures.forEach((feature, index) => {
        const rawOrbit = clamp((progress - index * orbitSlot) / orbitSlot);
        const orbitPosition = index === total - 1 ? rawOrbit * 0.5 : rawOrbit;
        const angle = orbitPosition * Math.PI * 2;
        const depth = (1 - Math.cos(angle)) / 2;
        const orbitX = -Math.sin(angle) * orbitAmplitude;
        const orbitY = lerp(-18, 18, depth);
        const orbitScale = lerp(0.34, 1.03, depth);
        const orbitOpacity = lerp(0.14, 1, depth);
        const orbitRotateY = Math.sin(angle) * 10;
        const orbitBlur = lerp(1.8, 0, depth);

        const targetX = lineupCenterShift + (index - (total - 1) / 2) * lineupSpacing;
        let x = lerp(orbitX, targetX, lineupAmount);
        let y = lerp(orbitY, 16, lineupAmount);
        let scale = lerp(orbitScale, lineupScale, lineupAmount);
        let opacity = lerp(orbitOpacity, 1, lineupAmount);
        let rotateY = lerp(orbitRotateY, 0, lineupAmount);
        let rotateZ = 0;
        let blur = lerp(orbitBlur, 0, lineupAmount);

        if (exitAmount > 0) {
          x += (index - (total - 1) / 2) * stackWidth * 0.045 * exitAmount;
          y = lerp(y, -64 - Math.abs(index - (total - 1) / 2) * 16, exitAmount);
          scale *= 1 - exitAmount * 0.14;
          opacity *= 1 - exitAmount;
          rotateZ = (index - (total - 1) / 2) * 3.5 * exitAmount;
          blur = exitAmount * 5;
        }

        feature.style.opacity = opacity.toFixed(4);
        feature.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)}) rotateY(${rotateY.toFixed(2)}deg) rotateZ(${rotateZ.toFixed(2)}deg)`;
        feature.style.filter = `blur(${blur.toFixed(2)}px)`;
        feature.style.zIndex = String(lineupAmount > 0.5 ? 20 + index : Math.round(depth * 100) + index);
        feature.classList.toggle('is-active', index === activeIndex);
        const allVisible = progress >= lineupStart && progress < exitEnd;
        const finalVisible = finalAmount > 0.72;
        feature.setAttribute('aria-hidden', String(finalVisible || (!allVisible && index !== activeIndex)));
      });

      const activeFeature = this.collectionFeatures[activeIndex];
      if (!activeFeature) return;
      if (this.collectionCounter) {
        this.collectionCounter.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      }
      if (this.collectionTitle) this.collectionTitle.textContent = activeFeature.dataset.title || '';
      if (this.collectionDescription) this.collectionDescription.textContent = activeFeature.dataset.description || '';

      const copyAmount = 1 - smoothstep((progress - 0.64) / 0.1);
      if (this.collectionCopy) {
        this.collectionCopy.style.opacity = copyAmount.toFixed(4);
        this.collectionCopy.style.transform = `translateY(${((1 - copyAmount) * -18).toFixed(2)}px)`;
        this.collectionCopy.setAttribute('aria-hidden', String(copyAmount <= 0.05));
      }
      if (this.collectionFinal) {
        this.collectionFinal.style.opacity = finalAmount.toFixed(4);
        this.collectionFinal.style.transform = `translateY(${((1 - finalAmount) * 24).toFixed(2)}px) scale(${(0.98 + finalAmount * 0.02).toFixed(4)})`;
        this.collectionFinal.classList.toggle('is-visible', finalAmount > 0.72);
        this.collectionFinal.setAttribute('aria-hidden', String(finalAmount <= 0.72));
      }
    }
  }

  customElements.define('clairabord-story', ClairabordStory);
})();
